/**
 * Authentication client for magic link authentication.
 * Handles sending magic links, verifying tokens, and session management.
 */

import { authManager } from './authManager';
import { apiClient } from './apiClient';

export interface SendMagicLinkResponse {
  success: boolean;
  message: string;
}

export interface VerifyMagicLinkResponse {
  sessionToken: string;
  email: string;
  playerId: string;
}

/**
 * Send a magic link to the specified email address.
 * Rate limited to 3 attempts per email per hour.
 * @param email - Email address to send magic link to
 * @param gameId - Optional game identifier (defaults to landing if omitted)
 * @param returnUrl - Optional URL to redirect to after verification (for localhost/preview builds)
 * @param consentToReminders - Optional consent for game reminders and marketing emails
 */
export async function sendMagicLink(
  email: string,
  gameId?: string,
  returnUrl?: string,
  consentToReminders?: boolean
): Promise<SendMagicLinkResponse> {
  const body: any = { email };
  
  // Include gameId if provided (otherwise backend defaults to landing)
  if (gameId) {
    body.gameId = gameId;
  }
  
  // Include returnUrl if provided
  if (returnUrl) {
    body.returnUrl = returnUrl;
  }
  
  // Include consent if provided
  if (consentToReminders !== undefined) {
    body.consentToReminders = consentToReminders;
  }
  
  const response = await apiClient<SendMagicLinkResponse>('/auth/send-magic-link', {
    method: 'POST',
    body: JSON.stringify(body)
  });

  return response;
}

/**
 * Verify a magic link token and establish a session.
 * Returns session token and user details on success.
 * Magic links expire after 15 minutes and can only be used once.
 */
export async function verifyMagicLink(token: string): Promise<VerifyMagicLinkResponse> {
  const response = await apiClient<VerifyMagicLinkResponse>('/auth/verify-magic-link', {
    method: 'POST',
    body: JSON.stringify({ token })
  });

  // Store session data in localStorage
  authManager.setSession(response.sessionToken, response.email, response.playerId);

  return response;
}

/**
 * Log out the current user.
 * Clears session data from localStorage.
 */
export function logout(): void {
  authManager.clearSession();
  // Optionally reload the page to reset app state
  window.location.reload();
}
